import { isArrayOfFloatArray } from "../../types";
import { Bounds } from "../geometry.bounds";
import { Cartesian2, Cartesian3 } from "../geometry.cartesian";
import { isArrayOfCartesianArray, RegionCode } from "../geometry.interfaces";
import { AbstractShape } from "./geometry.shape";
import { ShapeType } from "./geometry.shapes.interfaces";
export class Polyline extends AbstractShape {
    static ArraysEqual(arr1, arr2) {
        if (arr1.length !== arr2.length) {
            return false;
        }
        for (let i = 0; i < arr1.length; i++) {
            if (arr1[i] !== arr2[i]) {
                return false;
            }
        }
        return true;
    }
    static FromFloats(points, stride = 3) {
        if (isArrayOfFloatArray(points)) {
            const multipolyline = [];
            for (let i = 0; i < points.length; i++) {
                const tmp = points[i];
                const p = [];
                for (let j = 0; j < tmp.length; j += stride) {
                    p.push(new Cartesian3(tmp[j], tmp[j + 1], stride > 2 ? tmp[j + 2] : 0));
                }
                multipolyline.push(new Polyline(p));
            }
            return multipolyline;
        }
        const p = [];
        for (let i = 0; i < points.length; i += stride) {
            p.push(new Cartesian3(points[i], points[i + 1], stride > 2 ? points[i + 2] : 0));
        }
        return new Polyline(p);
    }
    static FromPoints(points) {
        if (isArrayOfCartesianArray(points)) {
            return Polyline.FromPoints(points.flat());
        }
        return new Polyline(points);
    }
    constructor(p, type) {
        super(type ?? ShapeType.Polyline);
        this._points = p;
    }
    get points() {
        return this._points;
    }
    clip(clipArea) {
        if (clipArea.containsBounds(this.boundingBox)) {
            return this;
        }
        return this._clipPolyline(clipArea);
    }
    _clipPolyline(clipArea) {
        if (clipArea.intersects(this.boundingBox)) {
            const polylines = [];
            let points = [];
            const codes = this._points.map((p) => Cartesian2.ComputeCode(p, clipArea));
            let a = this._points[0];
            let codea = codes[0];
            let inside = codea == 0;
            for (let i = 1; i < codes.length; i++) {
                const b = this._points[i];
                const codeb = codes[i];
                if (inside) {
                    points.push(this._buildPoint(a.x, a.y, a.z));
                    if (codeb != 0) {
                        const intersection = this._computeIntersectionToRef(clipArea, a, b, codeb, this._buildPoint(0, 0, 0));
                        points.push(intersection);
                        polylines.push(new Polyline(points));
                        points = [];
                        inside = !inside;
                    }
                }
                else {
                    if (codeb == 0) {
                        const intersection = this._computeIntersectionToRef(clipArea, a, b, codea, this._buildPoint(0, 0, 0));
                        points.push(intersection);
                        inside = !inside;
                    }
                }
                a = b;
                codea = codeb;
            }
            if (inside) {
                points.push(this._buildPoint(a.x, a.y, a.z));
            }
            if (points.length) {
                polylines.push(new Polyline(points));
            }
            return polylines.length ? (polylines.length == 1 ? polylines[0] : polylines) : undefined;
        }
        return undefined;
    }
    _computeIntersectionToRef(clipArea, a, b, code_out, ref) {
        const x1 = a.x;
        const y1 = a.y;
        const x2 = b.x;
        const y2 = b.y;
        if (code_out & RegionCode.TOP) {
            const y_max = clipArea.ymax;
            ref.x = x1 + ((x2 - x1) * (y_max - y1)) / (y2 - y1);
            ref.y = y_max;
        }
        else if (code_out & RegionCode.BOTTOM) {
            const y_min = clipArea.ymin;
            ref.x = x1 + ((x2 - x1) * (y_min - y1)) / (y2 - y1);
            ref.y = y_min;
        }
        else if (code_out & RegionCode.RIGHT) {
            const x_max = clipArea.xmax;
            ref.y = y1 + ((y2 - y1) * (x_max - x1)) / (x2 - x1);
            ref.x = x_max;
        }
        else if (code_out & RegionCode.LEFT) {
            const x_min = clipArea.xmin;
            ref.y = y1 + ((y2 - y1) * (x_min - x1)) / (x2 - x1);
            ref.x = x_min;
        }
        return ref;
    }
    _buildPoint(x, y, z) {
        return new Cartesian3(x ?? 0, y ?? 0, z ?? 0);
    }
    _buildBounds() {
        return Bounds.FromPoints2(...this._points);
    }
    _getPoints() {
        return this._points;
    }
}
//# sourceMappingURL=geometry.polyline.js.map